import { useEffect } from 'react';
import { EntityAnalysisModalTab } from '../../../shared/constants';

interface UseEntityAnalysisModalKeyboardProps {
  isOpen: boolean;
  onClose: () => void;
  setActiveTab: (tab: EntityAnalysisModalTab) => void;
  activeTab: EntityAnalysisModalTab;
}

/**
 * Custom hook to handle keyboard shortcuts for the EntityAnalysisModal
 *
 * Keyboard shortcuts:
 * - Escape: Close modal
 * - Tab: Switch to next tab
 * - Shift+Tab: Switch to previous tab
 * - Arrow Right: Switch to next tab
 * - Arrow Left: Switch to previous tab
 *
 * @param isOpen - Whether the modal is currently open
 * @param onClose - Function to close the modal
 * @param setActiveTab - Function to change the active tab
 */
export const useEntityAnalysisModalKeyboard = ({
  isOpen,
  onClose,
  setActiveTab,
  activeTab,
}: UseEntityAnalysisModalKeyboardProps) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      // Escape key - close modal
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      // Tab navigation (Tab for next, Shift+Tab for previous)
      if (e.key === 'Tab') {
        // Only handle Tab when focus is within the tabs area or modal
        const modalElement = document.querySelector('[role="dialog"]');
        const tabListElement = modalElement?.querySelector('[role="tablist"]');

        if (tabListElement && tabListElement.contains(document.activeElement)) {
          e.preventDefault();

          if (e.shiftKey) {
            // Shift+Tab - go to previous tab
            if (activeTab === EntityAnalysisModalTab.EDRDetails) {
              setActiveTab(EntityAnalysisModalTab.EntityOverview);
            }
          } else {
            // Tab - go to next tab
            if (activeTab === EntityAnalysisModalTab.EntityOverview) {
              setActiveTab(EntityAnalysisModalTab.EDRDetails);
            }
          }
          return;
        }
      }

      // Arrow key navigation
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (activeTab === EntityAnalysisModalTab.EntityOverview) {
          setActiveTab(EntityAnalysisModalTab.EDRDetails);
        }
        return;
      }

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (activeTab === EntityAnalysisModalTab.EDRDetails) {
          setActiveTab(EntityAnalysisModalTab.EntityOverview);
        }
        return;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, onClose, setActiveTab, activeTab]);
};
